"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ToolEngine = void 0;
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const streem_sdk_protobuf_1 = require("streem-sdk-protobuf");
const logger_1 = require("../../../util/logger");
class ToolEngine {
    constructor(laserData, drawData) {
        this.laserPosSubject = new rxjs_1.BehaviorSubject([]);
        this.laserPositionOffset = this.laserPosSubject.asObservable();
        this.drawPosSubject = new rxjs_1.BehaviorSubject([
            [],
            0,
        ]);
        this.drawPositionOffset = this.drawPosSubject.asObservable();
        this.unsubscribe = new rxjs_1.Subject();
        /**
         * Consumes a stream of draw events and makes a 1 dimension array of data
         * After 4500ms of no activity, the array is cleared
         * @param source An observable of draw data
         */
        this.handleDrawData = (source) => {
            const drawLength = this.lengthHelper(Infinity);
            // store all the drawings into a 1D array
            const drawings = source.pipe((0, operators_1.scan)((acc, curr) => {
                const shouldResetBuffer = acc.length > drawLength.get();
                if (shouldResetBuffer) {
                    acc = [];
                }
                if (curr.eventType === streem_sdk_protobuf_1.streem.room.ToolEventType.TOOL_EVENT_TYPE_HIDE &&
                    acc.length > 0 &&
                    acc[acc.length - 1].eventType !== streem_sdk_protobuf_1.streem.room.ToolEventType.TOOL_EVENT_TYPE_HIDE) {
                    const previousX = acc[acc.length - 1].xPercent;
                    const previousY = acc[acc.length - 1].yPercent;
                    acc.push(Object.assign({ xPercent: previousX, yPercent: previousY }, curr));
                }
                if (curr.eventType === streem_sdk_protobuf_1.streem.room.ToolEventType.TOOL_EVENT_TYPE_MOVE) {
                    acc.push(curr);
                }
                drawLength.set(acc.length);
                return acc;
            }, []));
            // remove all the drawings after 4500ms of no drawing
            const eraseDrawings = drawings.pipe((0, operators_1.switchMap)(() => (0, rxjs_1.timer)(4500).pipe((0, operators_1.switchMap)(() => (0, rxjs_1.of)([]).pipe((0, operators_1.tap)(e => drawLength.set(e.length)))))));
            // record the time since the last drawing. As time increases, the drawing will begin to fade away
            // timer function emits the first event at 0 and then every 50 pulses
            // takeWhile emits while x < 80
            const resetTimer = new rxjs_1.Subject();
            const ageOfDrawings = resetTimer
                .asObservable()
                .pipe((0, operators_1.switchMap)(() => (0, rxjs_1.timer)(0, 50).pipe((0, operators_1.takeWhile)(x => x <= 80))));
            const selfErasingDrawings = (0, rxjs_1.merge)(drawings.pipe((0, operators_1.tap)(() => resetTimer.next(undefined))), eraseDrawings);
            // Return the drawings, and their age so they can fade away by renderer
            return (0, rxjs_1.combineLatest)([selfErasingDrawings, ageOfDrawings]);
        };
        /**
         * Take in individual laser events and store the last 8 events into an array
         * After some milliseconds of no activity, begin to reduce these
         * events one by one every 16 ms.
         * @param source An Observable of laser events
         */
        this.handleLaserData = (source) => {
            const laserLength = this.lengthHelper(8);
            // Immediately collect the most recent laserLengthManager.limit coords
            const drawLaser = source.pipe((0, operators_1.scan)((acc, curr) => {
                const shouldResetBuffer = acc.length > laserLength.get();
                if (shouldResetBuffer) {
                    acc = [];
                }
                if (curr.eventType === streem_sdk_protobuf_1.streem.room.ToolEventType.TOOL_EVENT_TYPE_MOVE) {
                    acc.push({
                        xPercent: curr.xPercent,
                        yPercent: curr.yPercent,
                    });
                }
                acc = acc.slice(-laserLength.limit);
                laserLength.set(acc.length);
                return acc;
            }, []));
            // 80ms after the user has stopped drawing, we should begin erasing the tail
            const eraseLaser = drawLaser.pipe((0, operators_1.switchMap)(coords => (0, rxjs_1.timer)(80).pipe((0, operators_1.exhaustMap)(() => new rxjs_1.Observable(subscriber => {
                function delayEmit(nextCoords, multiplier) {
                    setTimeout(() => subscriber.next(nextCoords), 32 * multiplier);
                }
                for (let i = 0; i <= coords.length; i++) {
                    const nextEmit = coords.slice(i);
                    laserLength.set(nextEmit.length);
                    delayEmit(nextEmit, i);
                }
            })))));
            return (0, rxjs_1.merge)(drawLaser, eraseLaser);
        };
        this.lengthHelper = (limit) => {
            let length = 0;
            return {
                limit,
                set: (nextLength) => {
                    length = nextLength > limit ? limit : nextLength;
                },
                get: () => length,
            };
        };
        this.getPixelCoordinate = (offset, buffer) => ({
            x: (offset.xPercent / 100) * buffer.width,
            y: (offset.yPercent / 100) * buffer.height,
        });
        laserData
            .pipe(this.handleLaserData, (0, operators_1.takeUntil)(this.unsubscribe))
            .subscribe(offset => this.laserPosSubject.next(offset));
        drawData
            .pipe(this.handleDrawData, (0, operators_1.takeUntil)(this.unsubscribe))
            .subscribe(offset => this.drawPosSubject.next(offset));
    }
    disconnect() {
        logger_1.logger.debug('Tool Engine clearing subscriptions');
        this.unsubscribe.next(undefined);
    }
    render({ buffer }) {
        this.renderLaser(buffer);
        this.renderDraw(buffer);
    }
    renderDraw(buffer) {
        const [drawings, timeRemaining] = [...this.drawPosSubject.value];
        const points = drawings.map(drawUpdate => (Object.assign({ type: drawUpdate.eventType }, this.getPixelCoordinate(drawUpdate, buffer))));
        if (!points.length) {
            return;
        }
        const opacity = (80 - timeRemaining) / 80;
        const ctx = buffer.getContext('2d');
        ctx.save();
        ctx.lineJoin = 'round';
        ctx.lineWidth = 7;
        ctx.strokeStyle = `rgba(0, 140, 255, ${timeRemaining < 30 ? 1 : opacity})`;
        for (let i = 0; i < points.length - 1; i++) {
            if (points[i].type === streem_sdk_protobuf_1.streem.room.ToolEventType.TOOL_EVENT_TYPE_MOVE) {
                ctx.beginPath();
                ctx.moveTo(points[i].x, points[i].y);
                ctx.lineTo(points[i + 1].x, points[i + 1].y);
                ctx.stroke();
            }
            if (points[i].type === streem_sdk_protobuf_1.streem.room.ToolEventType.TOOL_EVENT_TYPE_HIDE) {
                ctx.stroke();
                ctx.closePath();
            }
        }
        ctx.restore();
        // clean up memory sooner than the garbage collector
        points.splice(0, points.length);
    }
    renderLaser(buffer) {
        const points = [...this.laserPosSubject.value].map(message => this.getPixelCoordinate(message, buffer));
        if (!points.length) {
            return;
        }
        const ctx = buffer.getContext('2d');
        ctx.save();
        ctx.strokeStyle = '#e74300';
        ctx.fillStyle = '#e74300';
        // Draw laser head
        const head = points[points.length - 1];
        ctx.beginPath();
        ctx.arc(head.x, head.y, 5, 0, 360);
        ctx.fill();
        ctx.closePath();
        // Draw tail
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#e74300';
        for (let i = 0; i < points.length - 1; i++) {
            ctx.beginPath();
            ctx.lineWidth = 5 + i * 0.55;
            ctx.moveTo(points[i].x, points[i].y);
            ctx.lineTo(points[i + 1].x, points[i + 1].y);
            ctx.closePath();
            ctx.stroke();
        }
        ctx.restore();
        // clean up memory sooner than the garbage collector
        points.splice(0, points.length);
    }
}
exports.ToolEngine = ToolEngine;
//# sourceMappingURL=tool_engine.js.map